import { BuoyReading, getLatestReading, STALE_READING_MINUTES } from "./buoys.js";

/* ---------- Types ---------- */

export type SurfRating = "FLAT" | "POOR" | "FAIR" | "FAIR_PLUS" | "GOOD" | "EPIC";

/** 1–5 star rating, MSW-style */
export type StarRating = 0 | 1 | 2 | 3 | 4 | 5;

export interface SurfConditions {
  rating: SurfRating;
  stars: StarRating;
  label: string;
  emoji: string;
  waveHeight: number | null;
  period: number | null;
  direction: number | null;
  waterTemp: number | null;
  swellIncoming: boolean;
}

export const RATING_CONFIG: Record<
  SurfRating,
  { label: string; emoji: string; color: string; stars: StarRating }
> = {
  FLAT: { label: "Flat", emoji: "🏖️", color: "#5c7a99", stars: 0 },
  POOR: { label: "Poor", emoji: "😐", color: "#e0a04f", stars: 1 },
  FAIR: { label: "Fair", emoji: "👍", color: "#c9b84c", stars: 2 },
  FAIR_PLUS: { label: "Fair+", emoji: "👍", color: "#d4c94f", stars: 3 },
  GOOD: { label: "Good", emoji: "🤙", color: "#4fc3f7", stars: 4 },
  EPIC: { label: "Epic", emoji: "🔥", color: "#66bb6a", stars: 5 },
};

/* ---------- Direction Helpers ---------- */

/** Normalise angular difference to 0–180° */
function angleDiff(a: number, b: number): number {
  let d = Math.abs(a - b) % 360;
  if (d > 180) d = 360 - d;
  return d;
}

/**
 * Piecewise-linear interpolation between (x, y) breakpoints. Clamps to
 * the endpoint values when `x` falls outside the defined range. Keeps
 * rating curves continuous so a 1° / 1s / 1 km/h change in input never
 * flips the rating a star or two.
 */
function piecewiseLinear(
  x: number,
  points: ReadonlyArray<readonly [number, number]>,
): number {
  if (x <= points[0][0]) return points[0][1];
  if (x >= points[points.length - 1][0]) return points[points.length - 1][1];
  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];
    if (x >= x1 && x <= x2) {
      const t = (x - x1) / (x2 - x1);
      return y1 + t * (y2 - y1);
    }
  }
  return points[points.length - 1][1];
}

/**
 * Swell-exposure modifier as a continuous function of the angle between
 * the swell direction and the shore normal.
 *
 * Plateau for near-head-on swell (full score inside 30°), ramps down to
 * 0.5 at parallel-to-coast (90°), then keeps ramping to 0.15 for
 * backside swell. The previous 0.6 floor past 90° over-scored swells
 * hitting the back of a peninsula (e.g. a southern-ocean swell on a
 * north-facing coast) — physically that arrives only through
 * diffraction and is a small fraction of offshore energy.
 *
 * shoreNormal = direction the coast faces (out to sea).
 * swellDir    = direction swell is coming FROM (meteorological).
 */
function swellExposureFactor(swellDir: number, shoreNormal: number): number {
  const diff = angleDiff(swellDir, shoreNormal);
  return piecewiseLinear(diff, [
    [0, 1.0],
    [30, 1.0], // plateau: head-on swell gets full score
    [90, 0.5], // parallel-to-coast → half energy via refraction
    [135, 0.2], // mostly shadowed
    [180, 0.15], // pure backside — diffraction leakage only
  ]);
}

/* ---------- Period Quality ---------- */

/**
 * Period quality multiplier as a continuous function of period (s).
 *
 * Long-period ground swells (14s+) are dramatically more powerful than
 * short-period wind swell (6–8s). The old code had hard thresholds,
 * which meant a period changing from 7.99s to 8.00s jumped the
 * multiplier from 0.75 to 1.0 — a 33% swing on a 0.01s step. With
 * piecewise-linear interpolation the transition is smooth.
 *
 * Breakpoints match the old plateau values so known scenarios (2ft @
 * 8s, 5ft @ 15s, etc.) keep the same rating; only the space *between*
 * breakpoints changes.
 */
function periodMultiplier(period: number): number {
  return piecewiseLinear(period, [
    [2, 0.4], // choppy wind swell floor
    [6, 0.75],
    [8, 1.0],
    [10, 1.1],
    [12, 1.2],
    [14, 1.5],
    [16, 1.65],
  ]);
}

/* ---------- Wind Penalty ---------- */

/**
 * Wind penalty: reduces quality score based on speed and direction.
 *
 * Uses cosine interpolation for smooth transitions between onshore/offshore.
 * No more hard 1-point jumps at direction boundaries.
 *
 * Returns a value from ~-2 (strong onshore) to +0.5 (light offshore).
 */
function windPenalty(
  windSpeedKmh: number,
  windDir: number | null,
  shoreNormal: number | null,
): number {
  if (windSpeedKmh <= 5) return 0.3; // glass-off conditions, slight bonus

  // Smooth direction factor: 0 = pure onshore, 1 = pure offshore.
  // cos(0°)=1 (onshore), cos(180°)=-1 (offshore) → negate and normalize.
  // Default to 0.25 (mostly onshore) when direction data is missing.
  let dirFactor = 0.25;
  if (windDir != null && shoreNormal != null) {
    const diff = angleDiff(windDir, shoreNormal);
    dirFactor = (-Math.cos((diff * Math.PI) / 180) + 1) / 2;
  }

  // Speed curves were previously hard steps (≤10 → -0.3, ≤20 → -1.0, …),
  // so a 0.01 km/h change could swing the score 0.7 points — the same
  // cliff the direction handling above was rewritten to avoid. The
  // breakpoints match the old step values at each step's upper edge, so
  // known scenarios keep their ratings; only the space between is now
  // interpolated.

  // Onshore penalty curve (dirFactor = 0)
  const onshoreVal = piecewiseLinear(windSpeedKmh, [
    [5, -0.3],
    [10, -0.3],
    [20, -1.0],
    [30, -1.5],
    [40, -2.0],
  ]);

  // Offshore bonus curve (dirFactor = 1)
  const offshoreVal = piecewiseLinear(windSpeedKmh, [
    [5, 0.5],
    [15, 0.5],
    [25, 0.2],
    [35, -0.3],
    [45, -1.0],
  ]);

  // Smooth interpolation between onshore and offshore
  return onshoreVal + dirFactor * (offshoreVal - onshoreVal);
}

/* ---------- Swell Exposure Modifier ---------- */

/**
 * Adjust score based on whether the swell is actually reaching this coast.
 * A perfectly aligned swell gets full score. A blocked swell is heavily penalized.
 */
function swellExposureModifier(
  swellDir: number | null,
  shoreNormal: number | null,
): number {
  if (swellDir == null || shoreNormal == null) return 1.0; // no data, assume full
  return swellExposureFactor(swellDir, shoreNormal);
}

/* ---------- Gust Penalty ---------- */

/**
 * Additional chop penalty for gusty wind. Sustained 15 km/h is far
 * cleaner than 15 km/h with 30 km/h gusts — the gusts are what actually
 * hit the face and chop it up, so a direction-only wind model
 * underestimates how badly a gusty day breaks down.
 *
 * Returns a non-positive number; zero when conditions aren't gusty
 * enough to matter (light base wind, or gust/speed ratio under 1.3
 * which is within normal variability).
 */
function gustPenalty(speedKmh: number, gustKmh: number | null | undefined): number {
  if (gustKmh == null || gustKmh <= speedKmh) return 0;
  if (speedKmh < 8) return 0; // light base wind → gusts rarely last long enough to chop a face
  const ratio = gustKmh / speedKmh;
  if (ratio <= 1.3) return 0;
  // Ramp penalty from 1.3 (0) up to 2.0 (-0.8), then cap.
  const normalized = Math.min(1, (ratio - 1.3) / 0.7);
  return -0.8 * normalized;
}

/* ---------- Core Rating Function ---------- */

export interface RatingInput {
  /** Wave height in feet */
  waveHeightFt: number | null;
  /** Wave period in seconds */
  period: number | null;
  /** Wind speed in km/h */
  windSpeedKmh?: number | null;
  /** Peak gust speed in km/h; used to apply an extra chop penalty. */
  windGustKmh?: number | null;
  /** Meteorological wind direction (degrees) */
  windDirectionDeg?: number | null;
  /** Swell direction (degrees, meteorological) */
  swellDirectionDeg?: number | null;
  /** Shore normal angle for this break */
  shoreNormal?: number | null;
  /** Optional skill level: max wave height (ft) the surfer is comfortable with.
   *  Waves above this get progressively penalized. */
  maxComfortFt?: number | null;
  /** Optional spot-calibration multiplier for known tide windows. */
  tideQualityFactor?: number | null;
}

/**
 * MSW-style surf quality rating.
 *
 * Produces a 0–5 star rating and a SurfRating label by combining:
 * 1. Base swell score from wave height
 * 2. Period quality multiplier (long period = much better)
 * 3. Swell direction exposure (is the swell reaching this coast?)
 * 4. Wind penalty/bonus (direction-aware)
 *
 * The raw score (0–10 scale) maps to stars and labels:
 *   0      → FLAT  (0 stars)
 *   0.1–2  → POOR  (1 star)
 *   2–4    → FAIR  (2 stars)
 *   4–5    → FAIR+ (3 stars)
 *   5–7    → GOOD  (4 stars)
 *   7+     → EPIC  (5 stars)
 */
export function rateSurfQuality(input: RatingInput): {
  rating: SurfRating;
  stars: StarRating;
} {
  const { waveHeightFt, period } = input;

  if (waveHeightFt == null || waveHeightFt < 0.3) {
    return { rating: "FLAT", stars: 0 };
  }

  // Null period = no data; use neutral multiplier (1.0x at 8s) rather than
  // penalizing as if it were confirmed short-period wind chop.
  const p = period ?? 8;

  // 1. Base score from wave height (0–7.5 range)
  //    Roughly: 1ft=1, 2ft=2.5, 3ft=3.5, 5ft=5, 8ft=6.5, 12ft+=7.5
  let baseScore: number;
  if (waveHeightFt >= 12) baseScore = 7.5;
  else if (waveHeightFt >= 8) baseScore = 6.5 + (waveHeightFt - 8) * 0.25;
  else if (waveHeightFt >= 5) baseScore = 5 + (waveHeightFt - 5) * 0.5;
  else if (waveHeightFt >= 3) baseScore = 3.5 + (waveHeightFt - 3) * 0.75;
  else if (waveHeightFt >= 1.5) baseScore = 1.5 + (waveHeightFt - 1.5) * 1.33;
  else baseScore = waveHeightFt;

  // 2. Period multiplier
  const periodMult = periodMultiplier(p);
  let score = baseScore * periodMult;

  // 3. Swell exposure
  score *= swellExposureModifier(
    input.swellDirectionDeg ?? null,
    input.shoreNormal ?? null,
  );

  // 4. Wind adjustment (null wind = no data → neutral, not a bonus)
  if (input.windSpeedKmh != null) {
    const penalty = windPenalty(
      input.windSpeedKmh,
      input.windDirectionDeg ?? null,
      input.shoreNormal ?? null,
    );
    // Scale onshore penalties with wave height — big blown-out waves are
    // disproportionately worse than small blown-out ones.
    const scaledPenalty =
      penalty < 0 && waveHeightFt != null && waveHeightFt > 3
        ? penalty * (1 + (waveHeightFt - 3) * 0.1)
        : penalty;
    score += scaledPenalty;

    // Gustiness is separate from sustained-wind direction: a light
    // offshore average still chops the face when the gusts come in at
    // 2×. Apply on top of the direction-aware penalty.
    score += gustPenalty(input.windSpeedKmh, input.windGustKmh);
  }

  if (input.tideQualityFactor != null) {
    score *= Math.max(0, Math.min(1, input.tideQualityFactor));
  }

  // 5. Skill-level penalty: waves above comfort threshold get penalized
  if (input.maxComfortFt != null && waveHeightFt > input.maxComfortFt) {
    const excess = (waveHeightFt - input.maxComfortFt) / input.maxComfortFt;
    score *= Math.max(0.3, 1 - excess);
  }

  // Clamp to 0–10
  score = Math.max(0, Math.min(10, score));

  // If there are real waves (>= 1ft), score should never map to FLAT
  if (waveHeightFt >= 1.0 && score < 1.0) score = 1.0;

  // Map to stars and rating
  let stars: StarRating;
  let rating: SurfRating;

  if (score < 0.5) {
    stars = 0;
    rating = "FLAT";
  } else if (score < 2) {
    stars = 1;
    rating = "POOR";
  } else if (score < 4) {
    stars = 2;
    rating = "FAIR";
  } else if (score < 5) {
    stars = 3;
    rating = "FAIR_PLUS";
  } else if (score < 7) {
    stars = 4;
    rating = "GOOD";
  } else {
    stars = 5;
    rating = "EPIC";
  }

  return { rating, stars };
}

/** How wind sits relative to shore — lets descriptors distinguish
 *  "blown out" (onshore chop) from "crossed up" (cross wind) from the
 *  glassy cases where a POOR rating might still be a fun longboard day. */
type WindFrame = "calm" | "offshore" | "onshore" | "cross" | "unknown";

function classifyWind(
  speedKmh: number | null | undefined,
  windDir: number | null | undefined,
  shoreNormal: number | null | undefined,
): WindFrame {
  if (speedKmh == null) return "unknown";
  if (speedKmh < 5) return "calm";
  if (windDir == null || shoreNormal == null) return "unknown";
  const diff = angleDiff(windDir, shoreNormal);
  if (diff < 60) return "onshore";
  if (diff > 120) return "offshore";
  return "cross";
}

/**
 * Per-case phrase pools. First entry of each is the canonical /
 * fallback — tests assert on keywords from index 0, and a call
 * without a seed returns index 0 deterministically.
 */
const PHRASES = {
  // EPIC
  epicXxl: [
    "Pumping — XXL and lined-up",
    "Maxing out — heavy and lined-up",
    "XXL groundswell — all-time",
    "Bomb swell — stacked to the horizon",
  ],
  epicGroomed: [
    "Pumping — groomed corduroy",
    "Firing and glassy — clean lines",
    "Corduroy to the horizon",
    "All-time — offshore and lined-up",
  ],
  epicFallback: [
    "Pumping — rare window",
    "Firing — drop everything",
    "All-time — don't sit this out",
    "Epic — a rare one",
  ],

  // GOOD
  goodLined: [
    "Firing — clean lined-up sets",
    "Proper grooming — sets stacking",
    "Lined up and firing",
    "Groomed and firing",
  ],
  goodGlassy: [
    "Glassy and firing",
    "Pure glass — firing",
    "Mirror-clean and firing",
    "Oil-glass and rideable",
  ],
  goodOffshore: [
    "Firing — clean and rideable",
    "Groomed and ready",
    "Clean and firing",
    "Firing — offshore and dialled",
  ],
  goodFallback: [
    "Firing — worth a session",
    "Firing — get out there",
    "Worth the paddle",
    "Firing — make time for it",
  ],

  // FAIR_PLUS
  fairPlusClean: [
    "Fun and clean — peaky sets",
    "Playful and clean",
    "Fun — clean little peaks",
    "Clean and peaky — worth a wax",
  ],
  fairPlusFallback: [
    "Fun — some set waves around",
    "Rideable fun",
    "Catch-em-if-you-can fun",
    "Some fun ones in between",
  ],

  // FAIR
  fairSmallClean: [
    "Small but clean — mellow session",
    "Small and glassy — easy session",
    "Mellow and clean",
    "Clean cruiser — small but fun",
  ],
  fairFallback: [
    "Workable if you're keen",
    "Fair — make the best of it",
    "Surfable if you're patient",
    "Not great, not terrible",
  ],

  // FLAT
  flat: [
    "Flat — nothing to paddle out for",
    "Lake day — flat",
    "Nothing to ride",
    "Flat as a pancake",
  ],

  // POOR
  victoryAtSea: [
    "Victory at sea — big and blown out",
    "Angry and big — save it",
    "Nuking and blown — not safe",
    "Storm surf — victory at sea",
  ],
  howlingOnshore: [
    "Howling onshore — blown out",
    "Gale-force onshore — pass",
    "Hammered by wind — blown out",
    "Onshore gale — unrideable",
  ],
  blownOut: [
    "Blown out — wind chopping the face",
    "Onshore chop — not really rideable",
    "Wind-ruined face — blown out",
    "Blown out — sideways spray everywhere",
  ],
  crossedUp: [
    "Crossed up — wind-chopped and disorganised",
    "Sideways chop — disorganised",
    "Cross-chop — peaks all mixed up",
    "Crossed wind — bumpy and sloppy",
  ],
  longboardLong: [
    "Small but glassy — longboarder's playground",
    "Longboard gold — small and clean",
    "Log day — small and glassy",
    "Fish-and-longboard territory — small but clean",
    "Small and groomed — get the log out",
  ],
  longboardTiny: [
    "Small and glassy — longboard territory",
    "Log special — ankle-high but clean",
    "Small and pristine — get the log",
    "Tiny and clean — longboard only",
  ],
  ankleBiters: [
    "Ankle biters — barely breaking",
    "Dribble — not really breaking",
    "Foam and ripples — tiny",
    "Barely a wave — wait for the tide",
  ],
  wrongDirection: [
    "Wrong direction — swell mostly shadowed",
    "Swell angled wrong — shadowed",
    "Swell window's closed — wrong direction",
    "Wrong direction — no energy making it in",
  ],
  shortPeriod: [
    "Short-period windswell — choppy and fast",
    "Wind chop — short-period swell",
    "Short-period — choppy and disorganised",
    "Windswell — too short-period to groom",
  ],
  smallClean: [
    "Small and clean — sneaky fun if you're keen",
    "Quietly fun — small and clean",
    "Sneaky — small but clean",
    "Surprise ones on offer — small and clean",
  ],
  mushy: [
    "Mushy — nothing with real punch",
    "Dribbly and weak — no punch",
    "Soft and weak — mushy",
    "Mush burgers — no real energy",
  ],
  poorFallback: [
    "Below par — not really firing",
    "Off day — nothing doing",
    "Middling — not happening today",
    "Not really firing — wait for better",
  ],
} as const;

function pickPhrase(pool: readonly string[], seed?: number | null): string {
  if (pool.length === 0) return "";
  if (seed == null) return pool[0];
  return pool[Math.abs(seed) % pool.length];
}

/**
 * Short surfer-shorthand phrase that names the dominant reason for a
 * given rating. The UI used to map every POOR rating to a single
 * "Blown out" phrase — which lied whenever the wave was simply small
 * or short-period with no wind. This version picks the most specific
 * cause from the same inputs the rater used, and — when a `seed` is
 * passed — varies the exact wording from a pool so the copy doesn't
 * feel canned. Callers typically derive the seed from `spot + hour
 * bucket` so it stays stable within a session but changes across
 * sessions / spots / days. Pass no seed to get the canonical phrase.
 */
export function describeConditions(
  rating: SurfRating,
  input: RatingInput,
  seed?: number | null,
): string {
  const wave = input.waveHeightFt;
  const speed = input.windSpeedKmh ?? null;
  const period = input.period;
  const swellDir = input.swellDirectionDeg ?? null;
  const shoreNormal = input.shoreNormal ?? null;
  const wind = classifyWind(speed, input.windDirectionDeg, shoreNormal);
  const swellOff =
    swellDir != null && shoreNormal != null ? angleDiff(swellDir, shoreNormal) : null;
  const cleanWind = wind === "calm" || wind === "offshore";
  const pick = (pool: readonly string[]) => pickPhrase(pool, seed);

  if (rating === "EPIC") {
    if (
      wave != null &&
      wave >= 8 &&
      period != null &&
      period >= 13 &&
      wind === "offshore"
    ) {
      return pick(PHRASES.epicXxl);
    }
    if (period != null && period >= 12 && wind === "offshore") {
      return pick(PHRASES.epicGroomed);
    }
    return pick(PHRASES.epicFallback);
  }

  if (rating === "GOOD") {
    if (period != null && period >= 12 && wind === "offshore") {
      return pick(PHRASES.goodLined);
    }
    if (wind === "calm") return pick(PHRASES.goodGlassy);
    if (wind === "offshore") return pick(PHRASES.goodOffshore);
    return pick(PHRASES.goodFallback);
  }

  if (rating === "FAIR_PLUS") {
    if (wind === "offshore") return pick(PHRASES.fairPlusClean);
    return pick(PHRASES.fairPlusFallback);
  }

  if (rating === "FAIR") {
    if (wave != null && wave < 4 && wind === "calm") {
      return pick(PHRASES.fairSmallClean);
    }
    return pick(PHRASES.fairFallback);
  }

  if (rating === "FLAT") return pick(PHRASES.flat);

  /* ----------------------- POOR ----------------------- */

  if (speed != null && speed > 30 && wind === "onshore") {
    if (wave != null && wave >= 6) return pick(PHRASES.victoryAtSea);
    return pick(PHRASES.howlingOnshore);
  }
  if (speed != null && speed > 15 && wind === "onshore") {
    return pick(PHRASES.blownOut);
  }
  if (speed != null && speed > 15 && wind === "cross") {
    return pick(PHRASES.crossedUp);
  }
  if (cleanWind && wave != null && wave < 2 && period != null && period >= 11) {
    return pick(PHRASES.longboardLong);
  }
  if (cleanWind && wave != null && wave < 1.5) {
    return pick(PHRASES.longboardTiny);
  }
  if (wave != null && wave < 1.5) {
    return pick(PHRASES.ankleBiters);
  }
  if (swellOff != null && swellOff >= 120) {
    return pick(PHRASES.wrongDirection);
  }
  if (period != null && period > 0 && period < 6) {
    return pick(PHRASES.shortPeriod);
  }
  if (cleanWind && wave != null && wave < 3) {
    return pick(PHRASES.smallClean);
  }
  if (wave != null && wave < 3) {
    return pick(PHRASES.mushy);
  }
  return pick(PHRASES.poorFallback);
}

/**
 * Simple deterministic hash used to seed descriptor phrase selection
 * from a stable input like `spot + hour bucket`, so the copy is
 * stable within a session but rotates across sessions/spots/days.
 */
export function descriptorSeed(...parts: (string | number)[]): number {
  const s = parts.join("|");
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return h;
}

/**
 * Legacy wrapper — keeps backward compat for existing callers.
 * Prefer rateSurfQuality() for new code.
 */
export function rateSurfConditions(
  waveHeightFt: number | null,
  period: number | null,
  windSpeedKmh?: number | null,
  windDirectionDeg?: number | null,
  swellDirectionDeg?: number | null,
  shoreNormal?: number | null,
): SurfRating {
  return rateSurfQuality({
    waveHeightFt,
    period,
    windSpeedKmh,
    windDirectionDeg,
    swellDirectionDeg,
    shoreNormal,
  }).rating;
}

/* ---------- Star Display Helpers ---------- */

/** Return filled/empty star string for display, e.g. "★★★☆☆" */
export function starString(stars: StarRating): string {
  return "★".repeat(stars) + "☆".repeat(5 - stars);
}

/** Color for a given star count */
export function starColor(stars: StarRating): string {
  if (stars >= 5) return RATING_CONFIG.EPIC.color;
  if (stars >= 4) return RATING_CONFIG.GOOD.color;
  if (stars >= 3) return RATING_CONFIG.FAIR_PLUS.color;
  if (stars >= 2) return RATING_CONFIG.FAIR.color;
  if (stars >= 1) return RATING_CONFIG.POOR.color;
  return RATING_CONFIG.FLAT.color;
}

/* ---------- Swell Detection ---------- */

/*
 * NOTE: `detectSwellIncoming` and `getSurfConditions` below are the
 * buoy-driven nowcast path. No production component calls them yet —
 * `CompactForecast` rates from the marine ensemble instead — but they
 * are kept (and covered by conditions/integration tests) as the
 * intended basis for a live "buoy says swell is arriving" feature.
 * If that feature is abandoned, delete both plus their tests.
 */

export function detectSwellIncoming(
  offshoreReadings: BuoyReading[] | undefined,
  nearshoreReadings: BuoyReading[] | undefined,
): boolean {
  const offshore = getLatestReading(offshoreReadings, STALE_READING_MINUTES);
  const nearshore = getLatestReading(nearshoreReadings, STALE_READING_MINUTES);

  if (!offshore?.waveHeight || !nearshore?.waveHeight || !offshore?.dominantPeriod) {
    return false;
  }

  const heightDiff = (offshore.waveHeight - nearshore.waveHeight) / nearshore.waveHeight;
  return heightDiff >= 0.3 && offshore.dominantPeriod >= 10;
}

/* ---------- Combined Conditions ---------- */

export function getSurfConditions(
  nearshoreReadings: BuoyReading[] | undefined,
  offshoreReadings: BuoyReading[] | undefined,
  shoreNormal?: number | null,
): SurfConditions {
  const latest = getLatestReading(nearshoreReadings, STALE_READING_MINUTES);

  const waveHeight = latest?.waveHeight ?? null;
  const period = latest?.dominantPeriod ?? null;
  const direction = latest?.meanWaveDirection ?? null;
  const waterTemp = latest?.waterTemp ?? null;

  // Convert buoy wind speed from m/s to km/h
  const windSpeedKmh = latest?.windSpeed != null ? latest.windSpeed * 3.6 : null;

  const { rating, stars } = rateSurfQuality({
    waveHeightFt: waveHeight,
    period,
    windSpeedKmh,
    windDirectionDeg: latest?.windDirection ?? null,
    swellDirectionDeg: direction,
    shoreNormal: shoreNormal ?? null,
  });
  const config = RATING_CONFIG[rating];
  const swellIncoming = detectSwellIncoming(offshoreReadings, nearshoreReadings);

  return {
    rating,
    stars,
    label: config.label,
    emoji: config.emoji,
    waveHeight,
    period,
    direction,
    waterTemp,
    swellIncoming,
  };
}
